import { useCallback, useEffect, useState } from "react";
import { Loader2, Ticket, ChevronRight, Film } from "lucide-react";
import { api } from "../../lib/api";
import { STATUS_STYLE, SEVERITY_STYLE, fmtDate } from "../../lib/support";

export const MyTickets = ({ onOpen, onReport }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/support/tickets");
      setTickets(data.tickets || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="border border-zinc-800 bg-zinc-900/40 p-12 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (!tickets.length) {
    return (
      <div className="border border-zinc-800 bg-zinc-900/40 p-12 text-center" data-testid="no-tickets">
        <Ticket className="w-7 h-7 text-zinc-700 mx-auto mb-4" />
        <div className="font-heading text-xl font-bold tracking-tight text-white">
          No support tickets yet
        </div>
        <p className="text-sm text-zinc-500 mt-2">
          Anything you report shows up here with its status.
        </p>
        <button
          type="button"
          onClick={onReport}
          data-testid="no-tickets-report-btn"
          className="mt-6 text-xs font-mono uppercase tracking-widest bg-white text-zinc-950 hover:bg-zinc-200 px-5 py-2.5 transition-colors"
        >
          Report a problem
        </button>
      </div>
    );
  }

  return (
    <div className="border border-zinc-800 divide-y divide-zinc-800" data-testid="my-tickets-list">
      {tickets.map((t) => (
        <button
          key={t.ticket_id}
          type="button"
          onClick={() => onOpen(t.ticket_id)}
          data-testid={`my-ticket-${t.ticket_id}`}
          className="w-full text-left px-5 md:px-7 py-5 bg-zinc-900/20 hover:bg-zinc-900/60 transition-colors duration-150 flex items-center gap-4"
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <span className="font-mono text-xs text-zinc-400">{t.ticket_id}</span>
              <span
                className={`text-[10px] font-mono uppercase tracking-widest border px-2 py-0.5 ${STATUS_STYLE[t.status] || ""}`}
              >
                {t.status_label}
              </span>
              <span
                className={`text-[10px] font-mono uppercase tracking-widest border px-1.5 py-0.5 ${SEVERITY_STYLE[t.reported_severity] || ""}`}
              >
                {t.reported_severity}
              </span>
            </div>
            <div className="text-white font-semibold tracking-tight mt-2">{t.category_label}</div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-[11px] font-mono text-zinc-600">
              <span>Opened {fmtDate(t.created_at)}</span>
              {t.job && (
                <span className="inline-flex items-center gap-1.5 max-w-[280px] truncate">
                  <Film className="w-3 h-3" /> {t.job.video_name}
                </span>
              )}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
        </button>
      ))}
    </div>
  );
};
